import axios from 'axios';
import type { ApiResponse } from '@/types/index';

/**
 * Structure du JWT d'opérateur (supporte les deux formats)
 */
export interface OperatorTokenPayload {
  // Format login.paladium.games
  minecraftUUID?: string;
  minecraftUsername?: string;
  // Format standard
  uuid?: string;
  username?: string;
  exp: number;       // Timestamp d'expiration
  iat?: number;      // Timestamp de création (optionnel)
  type?: string;
  session?: string;
  iss?: string;
}

/**
 * Format normalisé de l'opérateur
 */
export interface NormalizedOperatorInfo {
  uuid: string;
  username: string;
  exp: number;
}

class AuthService {
  private token: string | null = null;
  private readonly TOKEN_KEY = 'paladium_operator_token';

  /**
   * Définit le token JWT de l'opérateur (après authentification Microsoft)
   */
  setToken(token: string): void {
    this.token = token;
    this.saveToken(token);
  }

  /**
   * Récupère le token JWT stocké
   */
  getToken(): string | null {
    if (!this.token) {
      this.token = this.loadToken();
    }
    return this.token;
  }

  /**
   * Vérifie si l'opérateur est authentifié
   */
  isAuthenticated(): boolean {
    console.log('🔍 [authService] Checking if authenticated...');
    const token = this.getToken();
    console.log('🔍 [authService] Token from localStorage:', token ? 'EXISTS (length: ' + token.length + ')' : 'NULL');

    if (!token) {
      console.log('🔴 [authService] No token found');
      return false;
    }

    // Vérifier si le token n'est pas expiré
    try {
      const payload = this.decodeToken(token);
      console.log('🔍 [authService] Decoded payload:', payload);

      if (!payload) {
        console.log('🔴 [authService] Failed to decode token');
        return false;
      }

      const now = Math.floor(Date.now() / 1000);
      console.log('🔍 [authService] Token exp:', payload.exp, 'Current time:', now);

      if (payload.exp && payload.exp < now) {
        console.log('🔴 [authService] Token expired, clearing...');
        this.logout();
        return false;
      }

      console.log('✅ [authService] Token is valid');
      return true;
    } catch (error) {
      console.error('❌ [authService] Error checking token validity:', error);
      return false;
    }
  }

  /**
   * Vérifie le token auprès du backend (vérifie que l'UUID est autorisé)
   */
  async verifyToken(): Promise<boolean> {
    const token = this.getToken();
    if (!token) return false;

    try {
      const response = await axios.post<ApiResponse<{ valid: boolean }>>(
        '/api/auth/verify',
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      return response.data.success && response.data.data?.valid === true;
    } catch (error) {
      console.error('Error verifying token:', error);
      return false;
    }
  }

  /**
   * Récupère les informations de l'opérateur depuis le token (format normalisé)
   */
  getOperatorInfo(): NormalizedOperatorInfo | null {
    const token = this.getToken();
    if (!token) return null;

    const payload = this.decodeToken(token);
    if (!payload) return null;

    return this.normalizePayload(payload);
  }

  /**
   * Normalise le payload pour supporter les deux formats
   */
  private normalizePayload(payload: OperatorTokenPayload): NormalizedOperatorInfo | null {
    const uuid = payload.minecraftUUID || payload.uuid;
    const username = payload.minecraftUsername || payload.username;

    if (!uuid || !username) {
      console.error('❌ [normalizePayload] Missing UUID or username in payload:', payload);
      return null;
    }

    console.log('✅ [normalizePayload] Normalized:', { uuid, username });

    return {
      uuid,
      username,
      exp: payload.exp,
    };
  }

  /**
   * Décode le JWT (sans vérifier la signature - fait côté backend)
   */
  private decodeToken(token: string): OperatorTokenPayload | null {
    try {
      const parts = token.split('.');
      console.log('🔍 [decodeToken] Token parts count:', parts.length);

      if (parts.length !== 3) {
        console.error('🔴 [decodeToken] Invalid token format (expected 3 parts)');
        return null;
      }

      // Convertir base64url en base64 standard si nécessaire
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(base64));
      console.log('✅ [decodeToken] Successfully decoded payload');

      return payload as OperatorTokenPayload;
    } catch (error) {
      console.error('❌ [decodeToken] Error decoding token:', error);
      return null;
    }
  }

  /**
   * Déconnexion - efface le token
   */
  logout(): void {
    this.token = null;
    this.clearToken();
  }

  /**
   * Sauvegarde le token dans le localStorage
   */
  private saveToken(token: string): void {
    try {
      localStorage.setItem(this.TOKEN_KEY, token);
    } catch (error) {
      console.error('Error saving token:', error);
    }
  }

  /**
   * Charge le token depuis le localStorage
   */
  private loadToken(): string | null {
    try {
      return localStorage.getItem(this.TOKEN_KEY);
    } catch (error) {
      console.error('Error loading token:', error);
      return null;
    }
  }

  /**
   * Efface le token du localStorage
   */
  private clearToken(): void {
    try {
      localStorage.removeItem(this.TOKEN_KEY);
    } catch (error) {
      console.error('Error clearing token:', error);
    }
  }
}

export const authService = new AuthService();
